import { Chat, ChevronDown, ChevronRight, Document } from '@carbon/icons-react';
import { generateDiffFile } from '@git-diff-view/file';
import { DiffModeEnum, DiffView, SplitSide } from '@git-diff-view/react';
import '@git-diff-view/react/styles/diff-view.css';
import {
  alpha,
  Badge,
  Box,
  Collapse,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { InlineCommentBlock } from './inline-comment-block';
import type { FileToReview, ReviewComment, ReviewCommentSide } from './types';
import { getFileDiff } from './utils';

const ExtendLineTransitionBridge = ({ onReady }: { onReady: () => void }) => {
  useLayoutEffect(() => {
    onReady();
  }, [onReady]);

  return null;
};

interface FileDiffCardProps {
  file: FileToReview;
  comments: ReviewComment[];
  onAddComment: (
    filename: string,
    lineNumber: number,
    side: ReviewCommentSide,
    content: string,
  ) => void;
  onEditComment: (id: string, content: string) => void;
  onDeleteComment: (id: string) => void;
  onRetryComment?: (id: string) => void;
  isDisabled?: boolean;
  defaultExpanded?: boolean;
  id?: string;
}

export const FileDiffCard = forwardRef<HTMLDivElement, FileDiffCardProps>(
  (
    {
      file,
      comments,
      onAddComment,
      onEditComment,
      onDeleteComment,
      onRetryComment,
      isDisabled = false,
      defaultExpanded = true,
      id,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    // Track which line+side has the input widget open (only one at a time)
    // Key is `${lineNumber}-${side}`, e.g. "3-old" or "5-new"
    // Existing comments are shown via extendData/renderExtendLine, not here
    const [openWidget, setOpenWidget] = useState<{
      lineNumber: number;
      side: ReviewCommentSide;
    } | null>(null);
    // After the first comment on a line, git-diff-view applies `extendData` via an effect, so the
    // extend line can appear one paint late. To avoid a flash, we keep rendering the new comment
    // in the widget row until the extend line is confirmed active.
    const [widgetTransition, setWidgetTransition] = useState<{
      lineNumber: number;
      side: ReviewCommentSide;
    } | null>(null);
    const widgetCloseFnsRef = useRef<Record<string, () => void>>({});

    // Generate diff file from old/new content
    const diffFile = useMemo(() => {
      const df = generateDiffFile(
        file.filename,
        file.oldCode,
        file.filename,
        file.newCode,
        file.programmingLanguage || 'plaintext',
        file.programmingLanguage || 'plaintext',
      );
      df.init();
      df.buildSplitDiffLines();
      df.buildUnifiedDiffLines();
      return df;
    }, [file]);

    // Group comments by line number and side (key: `${lineNumber}-${side}`)
    const commentsByLineAndSide = useMemo(
      () =>
        comments.reduce(
          (acc, comment) => {
            const key = `${comment.lineNumber}-${comment.side}`;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(comment);
            return acc;
          },
          {} as Record<string, ReviewComment[]>,
        ),
      [comments],
    );

    const finalizeTransitionToExtendLine = useCallback(
      (lineNumber: number, side: ReviewCommentSide) => {
        const key = `${lineNumber}-${side}`;
        widgetCloseFnsRef.current[key]?.();
        setWidgetTransition((current) =>
          current?.lineNumber === lineNumber && current?.side === side
            ? null
            : current,
        );
      },
      [],
    );

    // If the transition line loses all comments (e.g., user deleted the only comment), clear the
    // transition and close the widget row so the UI updates immediately.
    useLayoutEffect(() => {
      if (widgetTransition === null) return;
      const key = `${widgetTransition.lineNumber}-${widgetTransition.side}`;
      const currentLineComments = commentsByLineAndSide[key] || [];
      if (currentLineComments.length > 0) return;

      widgetCloseFnsRef.current[key]?.();
      setWidgetTransition(null);
    }, [commentsByLineAndSide, widgetTransition]);

    // Handle widget click (+ button) - only one widget open at a time (matches library behavior)
    const handleAddWidgetClick = useCallback(
      (lineNumber: number, side: ReviewCommentSide) => {
        if (isDisabled) return;
        // If we're transitioning a different line/side, stop suppressing the extend line.
        if (
          widgetTransition !== null &&
          (widgetTransition.lineNumber !== lineNumber ||
            widgetTransition.side !== side)
        ) {
          setWidgetTransition(null);
        }
        // Replace all widgets - library only supports one active widget at a time
        setOpenWidget({ lineNumber, side });
      },
      [isDisabled, widgetTransition],
    );

    // Handle submit comment - close widget after submission (comment will show via extendData)
    const handleSubmitComment = useCallback(
      (lineNumber: number, side: ReviewCommentSide, content: string) => {
        const key = `${lineNumber}-${side}`;
        const existingComments = commentsByLineAndSide[key] || [];
        const isFirstCommentOnLine = existingComments.length === 0;

        onAddComment(file.filename, lineNumber, side, content);
        // Close the input widget; comment becomes visible either via the transition widget row
        // (first comment) or immediately via renderExtendLine (subsequent comments).
        setOpenWidget(null);

        if (isFirstCommentOnLine) {
          setWidgetTransition({ lineNumber, side });
        } else {
          widgetCloseFnsRef.current[key]?.();
        }
      },
      [commentsByLineAndSide, file.filename, onAddComment],
    );

    // Handle cancel - close the input widget
    const handleCancelWidget = useCallback(
      (lineNumber: number, side: ReviewCommentSide) => {
        setOpenWidget(null);
        if (
          widgetTransition?.lineNumber === lineNumber &&
          widgetTransition?.side === side
        ) {
          setWidgetTransition(null);
        }
        const key = `${lineNumber}-${side}`;
        widgetCloseFnsRef.current[key]?.();
      },
      [widgetTransition],
    );

    // Handle delete - close widget if no more comments on this line+side
    const handleDeleteWithWidgetClose = useCallback(
      (id: string) => {
        const comment = comments.find((c) => c.id === id);
        if (comment) {
          const key = `${comment.lineNumber}-${comment.side}`;
          const remainingComments = comments.filter(
            (c) =>
              c.lineNumber === comment.lineNumber &&
              c.side === comment.side &&
              c.id !== id,
          );
          if (remainingComments.length === 0) {
            // Close widget if open for this line+side
            if (
              openWidget?.lineNumber === comment.lineNumber &&
              openWidget?.side === comment.side
            ) {
              setOpenWidget(null);
            }
            widgetCloseFnsRef.current[key]?.();
          }
        }
        onDeleteComment(id);
      },
      [comments, onDeleteComment, openWidget],
    );

    // Count additions and deletions
    const { additions, deletions } = getFileDiff(file);

    // Extend data for lines with comments - structure must be:
    // { oldFile: { [lineNum]: { data } }, newFile: { [lineNum]: { data } } }
    // Comments on deleted lines go in oldFile, comments on added/context lines go in newFile
    const extendDataMemo = useMemo(() => {
      const oldFile: Record<
        string,
        { data: { lineNumber: number; side: ReviewCommentSide } }
      > = {};
      const newFile: Record<
        string,
        { data: { lineNumber: number; side: ReviewCommentSide } }
      > = {};

      comments.forEach((comment) => {
        const lineNum = comment.lineNumber.toString();
        const target = comment.side === 'old' ? oldFile : newFile;
        if (!target[lineNum]) {
          target[lineNum] = {
            data: { lineNumber: comment.lineNumber, side: comment.side },
          };
        }
      });

      return { oldFile, newFile };
    }, [comments]);

    return (
      <Paper
        ref={ref}
        id={id}
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
          mb: 2,
        }}
      >
        {/* File header */}
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            borderBottom: isExpanded
              ? `1px solid ${theme.palette.divider}`
              : 'none',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <IconButton size='small' sx={{ p: 0 }}>
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </IconButton>

          <Document size={16} />

          <Typography
            variant='body2'
            sx={{
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontWeight: 500,
              flex: 1,
            }}
          >
            {file.filename}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {additions > 0 && (
              <Typography
                variant='caption'
                sx={{ color: theme.palette.success.main, fontWeight: 600 }}
              >
                +{additions}
              </Typography>
            )}
            {deletions > 0 && (
              <Typography
                variant='caption'
                sx={{ color: theme.palette.error.main, fontWeight: 600 }}
              >
                -{deletions}
              </Typography>
            )}
            {comments.length > 0 && (
              <Badge
                badgeContent={comments.length}
                color='primary'
                sx={{ ml: 1 }}
              >
                <Chat size={16} />
              </Badge>
            )}
          </Box>
        </Box>

        {/* Collapsible diff content */}
        <Collapse in={isExpanded}>
          <Box
            sx={{
              '& .diff-view-wrapper': {
                fontSize: '13px',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              },
            }}
          >
            <DiffView
              diffFile={diffFile}
              diffViewMode={DiffModeEnum.Unified}
              diffViewTheme={theme.palette.mode}
              diffViewHighlight
              diffViewAddWidget={!isDisabled}
              diffViewWrap={false}
              diffViewFontSize={13}
              onAddWidgetClick={(lineNumber, libSide) => {
                if (typeof lineNumber === 'number' && lineNumber > 0) {
                  const side: ReviewCommentSide =
                    libSide === SplitSide.old ? 'old' : 'new';
                  handleAddWidgetClick(lineNumber, side);
                }
              }}
              renderWidgetLine={({ lineNumber, side: libSide, onClose }) => {
                if (!lineNumber) return null;

                const side: ReviewCommentSide =
                  libSide === SplitSide.old ? 'old' : 'new';
                const key = `${lineNumber}-${side}`;
                widgetCloseFnsRef.current[key] = onClose;

                const lineComments = commentsByLineAndSide[key] || [];
                const isTransition =
                  widgetTransition?.lineNumber === lineNumber &&
                  widgetTransition?.side === side;
                const isInputOpen =
                  openWidget?.lineNumber === lineNumber &&
                  openWidget?.side === side;

                // During the first-comment transition, render comments in the widget row so there
                // is no intermediate paint where the comment isn't visible.
                if (isTransition) {
                  if (lineComments.length === 0) return null;
                  return (
                    <InlineCommentBlock
                      comments={lineComments}
                      showInput={false}
                      onSubmit={(content) =>
                        handleSubmitComment(lineNumber, side, content)
                      }
                      onCancel={() => handleCancelWidget(lineNumber, side)}
                      onEdit={onEditComment}
                      onDelete={handleDeleteWithWidgetClose}
                      onRetry={onRetryComment}
                      isDisabled={isDisabled}
                    />
                  );
                }

                // Widget line is for the input when "+" is clicked on a line WITHOUT existing comments
                if (!isInputOpen) return null;

                // If this line has existing comments, the input will be shown in renderExtendLine instead
                if (lineComments.length > 0) return null;

                return (
                  <InlineCommentBlock
                    comments={[]}
                    showInput={true}
                    inputOnly={true}
                    onSubmit={(content) =>
                      handleSubmitComment(lineNumber, side, content)
                    }
                    onCancel={() => handleCancelWidget(lineNumber, side)}
                    onEdit={onEditComment}
                    onDelete={handleDeleteWithWidgetClose}
                    onRetry={onRetryComment}
                    isDisabled={isDisabled}
                  />
                );
              }}
              extendData={extendDataMemo}
              renderExtendLine={({ data }) => {
                // Extend line shows existing comments, and input if widget is open (so input appears below comments)
                const extData = data as
                  | { lineNumber?: number; side?: ReviewCommentSide }
                  | undefined;
                if (!extData?.lineNumber || !extData?.side) return null;
                const lineNumber = extData.lineNumber;
                const side = extData.side;
                const key = `${lineNumber}-${side}`;
                const lineComments = commentsByLineAndSide[key] || [];

                // If we're transitioning this line+side, we now know the extend line is active.
                // We suppress rendering here until we close the widget row in a layout effect.
                if (
                  widgetTransition?.lineNumber === lineNumber &&
                  widgetTransition?.side === side
                ) {
                  return (
                    <ExtendLineTransitionBridge
                      onReady={() =>
                        finalizeTransitionToExtendLine(lineNumber, side)
                      }
                    />
                  );
                }

                if (lineComments.length === 0) return null;

                const isInputOpen =
                  openWidget?.lineNumber === lineNumber &&
                  openWidget?.side === side;

                return (
                  <InlineCommentBlock
                    comments={lineComments}
                    showInput={isInputOpen}
                    onSubmit={(content) =>
                      handleSubmitComment(lineNumber, side, content)
                    }
                    onCancel={() => handleCancelWidget(lineNumber, side)}
                    onEdit={onEditComment}
                    onDelete={handleDeleteWithWidgetClose}
                    onRetry={onRetryComment}
                    isDisabled={isDisabled}
                  />
                );
              }}
            />
          </Box>
        </Collapse>
      </Paper>
    );
  },
);

FileDiffCard.displayName = 'FileDiffCard';
